import { useTenantStore } from '@/stores/tenantStore'
import { FileTextOutlined } from '@ant-design/icons'
import { OrderKey } from '@api/hooks/useIntegrationLogs'
import { CrossroadsRoutingInfo } from '@api/hooks/useOrders'
import { Horizontal } from '@gravitate-js/excalibrr'

/**
 * The order number on the given tenant's side of the routing.
 *
 * Takes the tenant name rather than reading the store, so it's safe to call
 * from an AG Grid valueGetter -- those run outside React's render, where a
 * hook call throws.
 */
export function myOrderNumber(
  order_key: OrderKey | null | undefined,
  routing: CrossroadsRoutingInfo | null | undefined,
  tenantName: string | null | undefined,
): string | null {
  if (!order_key || !routing || !tenantName) {
    return null
  }
  if (tenantName == routing.origin_tenant_name) {
    return order_key.origin_order_number ?? null
  } else {
    return order_key.destination_order_number ?? null
  }
}

/** The number on the other side of the routing -- see myOrderNumber. */
export function partnerOrderNumber(
  order_key: OrderKey | null | undefined,
  routing: CrossroadsRoutingInfo | null | undefined,
  tenantName: string | null | undefined,
): string | null {
  if (!order_key || !routing || !tenantName) {
    return null
  }
  if (tenantName == routing.origin_tenant_name) {
    return order_key.destination_order_number ?? null
  } else {
    return order_key.origin_order_number ?? null
  }
}

export function OrderLinkRenderer({
  order_key,
  routing,
  myNumber,
}: {
  order_key: OrderKey | null | undefined
  routing: CrossroadsRoutingInfo | null | undefined
  myNumber: boolean
}) {
  // Ahead of every early return -- a hook can't sit behind a condition.
  const { selectedTenant } = useTenantStore()

  const orderNumber = myNumber
    ? myOrderNumber(order_key, routing, selectedTenant?.name)
    : partnerOrderNumber(order_key, routing, selectedTenant?.name)
  const orderId = order_key?.order_id
  if (!orderNumber && !orderId) return null

  const linkId = orderId ?? orderNumber
  const displayText = orderNumber ?? '-'
  const url = `/operations/orders/${encodeURIComponent(linkId!)}`

  return (
    <Horizontal gap={8} alignItems='center'>
      <a
        href={url}
        onClick={(e) => e.stopPropagation()}
        title='View order details'
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
          minWidth: 24,
          height: 24,
          color: 'var(--foreground)',
        }}
      >
        <FileTextOutlined />
      </a>
      <span>{displayText}</span>
    </Horizontal>
  )
}
